import { useDispatch, useSelector } from "react-redux";
import ModalContainer from "../../Components/ModalContainer/ModalContainer";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import ArrowLeft from "../../Assets/Arrow left.svg";
import { useContext, useEffect, useState } from "react";
import { EditSizeChartContext } from "../../Context/EditSizeChartContext";
import {
    fetchSizeChart,
    fetchSizeCharts,
    setModal,
} from "../../Features/inventoryManagementSlice";
import {
    defaultTShirtFormObject,
    defaultTrousersFormObject,
    defaultWomenDressFormObject,
    defaultCoatsFormObject,
    defaultShoeFormObject,
    defaultHeelFormObject,
    defaultClothingSetFormObject,
} from "../AddSizeChart/SizeChartFormsData";
import { v4 as uuidv4 } from "uuid";
import SizesForm from "./Form/SizesForm";
import RowRemoveModalContainer from "./Modals/RowRemoveModal/RowRemoveModalContainer";
import { showToast } from "../../Components/Toaster/Toaster";
import { updateSizeChart } from "../../lib/utils/apiCalls";
import ColumnRemoveModalContainer from "./Modals/ColumnRemoveModal/ColumnRemoveModalContainer";
import AddColumn from "./Modals/AddColumn";
import Loading from "../../Components/Loading/Loading";
import Error from "../../Components/Error/Error";
import LoadingTransparent from '../../Components/Loading/LoadingTransparent';

function getDefaultFormByCategory(category) {
    switch (category) {
        case "T-Shirt, Shirt, Top":
            return { ...defaultTShirtFormObject, id: uuidv4() };
        case "Trousers, Phants, Jeans":
            return { ...defaultTrousersFormObject, id: uuidv4() };
        case "Women Dresses":
            return { ...defaultWomenDressFormObject, id: uuidv4() };
        case "Coat & Jacket":
            return { ...defaultCoatsFormObject, id: uuidv4() };
        case "Shoe & Sandals":
            return { ...defaultShoeFormObject, id: uuidv4() };
        case "High Heels":
            return { ...defaultHeelFormObject, id: uuidv4() };
        case "Clothing Set":
            return { ...defaultClothingSetFormObject, id: uuidv4() };
        default:
            return null;
    }
}

function EditSizeChart() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    const [updateLoading, setUpdateLoading] = useState(false);
    const defaultFormErrors = { name: "", sizes: "" };
    const [formErrors, setFormErrors] = useState(defaultFormErrors);

    const { activeModal: type, displayModal: open } = useSelector(
        (state) => state.inventoryManagement.modal
    );
    const { sizeChartDetails, loading } = useSelector(
        (state) => state.inventoryManagement.sizeChartDetails
    );

    const {
        selectedCategory,
        setSelectedCategory,
        sizeChartName,
        setSizeChartName,
        sizeChartFormData,
        setSizeChartFormData,
    } = useContext(EditSizeChartContext);

    function handleCancel() {
        if (sizeChartDetails?.sizes?.length > 0) {
            setSizeChartFormData(sizeChartDetails.sizes);
        }
    }

    function addNewRowToForm() {
        if (sizeChartFormData.length === 0) {
            const newSection = getDefaultFormByCategory(selectedCategory);
            if (newSection) setSizeChartFormData([newSection]);
            return;
        }

        // Dynamically copy keys from existing structure
        const referenceRow = sizeChartFormData[0];
        const newRow = { id: crypto.randomUUID() };

        Object.entries(referenceRow).forEach(([key, value]) => {
            if (key === "id") return;

            if (key === "sizeName") {
                newRow[key] = "";
            } else if (typeof value === "object" && value !== null && "cm" in value && "in" in value) {
                // For measurement fields
                newRow[key] = {
                    cm: "",
                    in: "",
                };

                // Preserve label for shoes/heels
                if (value.sizeName) newRow[key].sizeName = value.sizeName;
            } else {
                // Just copy primitive or unknown fields directly
                newRow[key] = "";
            }
        });

        setSizeChartFormData((prev) => [...prev, newRow]);
    }

    function addNewColumnToForm() {
        dispatch(setModal('edit-add-column'));
    }

    function handleRemoveRow() {
        dispatch(setModal("edit-remove-row"));
    }

    function handleRemoveColumn() {
        dispatch(setModal("edit-remove-column"));
    }

    useEffect(() => {
        if (params.chartId) dispatch(fetchSizeChart(params.chartId));
    }, [params.chartId, dispatch]);

    useEffect(() => {
        if (Object.keys(sizeChartDetails).length > 0) {
            setSelectedCategory(sizeChartDetails.chartType);
            setSizeChartName(sizeChartDetails.chartName);

            let sizes = sizeChartDetails.sizes;
            if (sizeChartDetails.chartType === "Clothing Set") {
                sizes = sizeChartDetails.sizes.map((row) => {
                    if (row.outSeam) {
                        const { outSeam, ...rest } = row;
                        return { ...rest, outseam: outSeam };
                    }
                    return row;
                });
            }
            setSizeChartFormData(sizes);
        }
    }, [sizeChartDetails, setSelectedCategory, setSizeChartName, setSizeChartFormData]);

    async function handleUpdate() {
        const validationErrors = {};

        if (!sizeChartName?.trim()) validationErrors.name = "Name is required";

        const hasEmptyField =
            sizeChartFormData?.some((row) =>
                Object.entries(row).some(([key, value]) => {
                    if (key === "id" || key === "sizeName") return false;
                    if (value && typeof value === "object" && "cm" in value && "in" in value)
                        return !value.cm || !value.in;
                    return false;
                })
            ) ?? false;

        if (hasEmptyField) validationErrors.sizes = "Empty field not allowed";

        if (Object.keys(validationErrors).length > 0) {
            setFormErrors(validationErrors);
            return;
        }

        if (sizeChartFormData.length === 0) {
            showToast("Add at least one size before saving.", "warning");
            return;
        }

        setUpdateLoading(true);
        setFormErrors(defaultFormErrors);

        let transformedSizes = sizeChartFormData;
        if (selectedCategory === "Clothing Set") {
            transformedSizes = sizeChartFormData.map((row) => {
                if (row.outseam) {
                    const { outseam, ...rest } = row;
                    return { ...rest, outSeam: outseam };
                }
                return row;
            });
        }

        const callBody = {
            chartName: sizeChartName.trim(),
            chartType: selectedCategory,
            sizes: transformedSizes,
        };

        try {
            const response = await updateSizeChart(callBody, sizeChartDetails?.id);
            if (response.status === 200) {
                showToast("Size chart updated.", "success");
                dispatch(fetchSizeCharts());
                setUpdateLoading(false);
                navigate(`${location.pathname.split("/").slice(0, -1).join("/")}`);
            } else {
                showToast("Failed to update size chart.", "error");
                setUpdateLoading(false);
            }
        } catch {
            showToast("Failed to update size chart.", "error");
            setUpdateLoading(false);
        }
    }

    if (loading === "loading") {
        return <Loading />;
    }

    if (loading === "failed") {
        return <Error />;
    }

    return (
        <div className="size-full overflow-y-scroll pb-16 outline-none">
            <div className="select-none flex items-center justify-between gap-6 relative m-[20px_19px_24px_20px]">
                <div className="w-fit flex items-center">
                    <Link to=".." className="p-[10px] flex items-center justify-center">
                        <img className="size-6 text-[#666666]" src={ArrowLeft} alt="Back" />
                    </Link>
                    <h1 className="p-[6px] text-[#121212] font-bold leading-8 text-[24px]">
                        {selectedCategory === "Trousers, Phants, Jeans"
                            ? "Trousers, Pants, Jeans"
                            : selectedCategory}
                    </h1>
                </div>
            </div>

            <div
                className="flex flex-col gap-4 mt-4 ml-16 mr-5"
            >
                <div className="flex flex-col gap-3">
                    <div
                        className="w-3/5 flex flex-col gap-2"
                    >
                        <div className="flex items-center justify-between">
                            <label
                                htmlFor="sizeChartName"
                                className="text-base text-[#121212] font-medium"
                            >
                                Name your Size Chart
                            </label>

                            {formErrors.name && (
                                <p className="text-[14px] leading-5 text-[#DC2626] select-none">
                                    {formErrors.name}
                                </p>
                            )}
                        </div>

                        <input
                            className="text-base font-normal px-3 py-4 rounded-[9px] bg-[#F5F5F5] border border-[#EAEAEA] outline-none focus:border-primaryColor placeholder:text-[#666666] text-[#121212]"
                            type="text"
                            id="sizeChartName"
                            name="sizeChartName"
                            placeholder="Enter the name here........"
                            value={sizeChartName}
                            onChange={(e) => setSizeChartName(e.target.value)}
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <p className="text-base text-[#121212] font-medium">
                            Enter accurate sizes into the chart and save.
                        </p>

                        {loading === "idle" || loading === "loading" ? (
                            <></>
                        ) : (
                            <div className="max-w-full overflow-x-scroll overscroll-y-none flex flex-col gap-2">
                                <SizesForm />
                            </div>
                        )}

                        {formErrors.sizes && (
                            <p className="text-[14px] leading-5 text-[#DC2626] select-none">
                                {formErrors.sizes}
                            </p>
                        )}

                        <div
                            className="w-3/5 flex items-center justify-between text-base font-medium text-[#1055EB]"
                        >
                            <div className="flex items-center gap-3">
                                <button className='outline-none' onClick={addNewRowToForm}>Add Row</button>

                                <button className='outline-none' onClick={addNewColumnToForm}>Add Column</button>
                            </div>

                            <div className="flex items-center gap-3">
                                <button className='outline-none' onClick={handleRemoveRow}>Remove Row</button>

                                <button className='outline-none' onClick={handleRemoveColumn}>Remove Column</button>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    className="full flex items-center gap-[10px] justify-end"
                >
                    <button
                        onClick={handleCancel}
                        className="px-5 py-[10px] rounded outline-none text-[#757575] text-base font-semibold"
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleUpdate}
                        className="px-5 py-[10px] rounded outline-none text-white bg-primaryColor hover:bg-primaryAccent text-base font-semibold"
                    >
                        Save
                    </button>
                </div>
            </div>

            {open && (
                <ModalContainer>
                    {type === "edit-remove-row" && <RowRemoveModalContainer />}

                    {type === "edit-add-column" && <AddColumn />}

                    {type === "edit-remove-column" && <ColumnRemoveModalContainer />}
                </ModalContainer>
            )}

            {updateLoading && <LoadingTransparent />}
        </div>
    );
}

export default EditSizeChart;
